import { useState, FormEvent, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import CaptchaInput from '@/components/base/CaptchaInput';
import AlertBox from '@/components/common/AlertBox';

const STORAGE_KEY = 'gtra_apply_submissions';

interface SubmissionRecord {
  email: string;
  submittedAt: number;
  orgName: string;
}

function saveSubmission(email: string, orgName: string) {
  const existing: SubmissionRecord[] = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
  const record: SubmissionRecord = { email: email.toLowerCase().trim(), orgName, submittedAt: Date.now() };
  const filtered = existing.filter((r) => r.email !== record.email);
  localStorage.setItem(STORAGE_KEY, JSON.stringify([...filtered, record]));
}

function findSubmission(email: string): SubmissionRecord | null {
  const existing: SubmissionRecord[] = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
  return existing.find((r) => r.email === email.toLowerCase().trim()) || null;
}

function getStageFromTime(submittedAt: number): number {
  const elapsed = Date.now() - submittedAt;
  const minutes = elapsed / 60000;
  if (minutes < 1) return 0;
  if (minutes < 5) return 1;
  if (minutes < 15) return 2;
  return 3;
}

interface TrackStage {
  labelZh: string;
  labelEn: string;
  descZh: string;
  descEn: string;
  icon: string;
}

const TRACK_STAGES: TrackStage[] = [
  { labelZh: '申请已提交', labelEn: 'Application Submitted', descZh: '我们已收到您的合作申请', descEn: 'We have received your application', icon: 'ri-checkbox-circle-line' },
  { labelZh: '资格初审', labelEn: 'Initial Screening', descZh: '核实机构信息与申请资格', descEn: 'Verifying institution details and eligibility', icon: 'ri-search-eye-line' },
  { labelZh: '材料深度评审', labelEn: 'In-depth Review', descZh: '委员会评估合作方向与战略匹配', descEn: 'Committee evaluating partnership fit', icon: 'ri-file-list-3-line' },
  { labelZh: '结果通知', labelEn: 'Result Notification', descZh: '审核结果将通过邮件告知您', descEn: 'Result will be notified via email', icon: 'ri-mail-check-line' },
];

function ApplicationTracker({ isZh }: { isZh: boolean }) {
  const [email, setEmail] = useState('');
  const [queried, setQueried] = useState(false);
  const [record, setRecord] = useState<SubmissionRecord | null>(null);
  const [stage, setStage] = useState(0);

  const handleQuery = () => {
    if (!email.trim()) return;
    const found = findSubmission(email.trim());
    setRecord(found);
    if (found) {
      setStage(getStageFromTime(found.submittedAt));
    }
    setQueried(true);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') handleQuery();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center pb-2">
        <div className="w-12 h-12 rounded-2xl bg-[#0A2540]/8 flex items-center justify-center mx-auto mb-3">
          <div className="w-6 h-6 flex items-center justify-center">
            <i className="ri-search-line text-[#0A2540] text-xl" />
          </div>
        </div>
        <h3 className="text-base font-bold text-[#0A2540] mb-1">
          {isZh ? '查询申请进度' : 'Track Your Application'}
        </h3>
        <p className="text-[#0A2540]/45 text-xs">
          {isZh ? '输入申请时使用的邮箱地址，查看审核状态' : 'Enter the email used during application to check review status'}
        </p>
      </div>

      {/* Email input */}
      <div className="flex gap-2">
        <input
          type="email"
          value={email}
          onChange={(e) => { setEmail(e.target.value); setQueried(false); }}
          onKeyDown={handleKeyDown}
          placeholder={isZh ? '请输入您的申请邮箱' : 'Enter your application email'}
          className="flex-1 px-3 py-2.5 rounded-lg border border-[#0A2540]/15 bg-white text-sm text-[#0A2540] placeholder-[#0A2540]/30 focus:outline-none focus:ring-2 focus:ring-[#0A2540]/20 transition-all"
        />
        <button
          onClick={handleQuery}
          disabled={!email.trim()}
          className="px-5 py-2.5 rounded-lg bg-[#0A2540] hover:bg-[#0A2540]/85 text-white text-sm font-medium transition-all cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isZh ? '查询' : 'Query'}
        </button>
      </div>

      {/* Results */}
      {queried && !record && (
        <div className="flex items-center gap-3 p-4 rounded-xl bg-amber-50 border border-amber-200/60">
          <div className="w-5 h-5 flex items-center justify-center shrink-0">
            <i className="ri-information-line text-amber-500" />
          </div>
          <div>
            <p className="text-sm font-medium text-amber-700">
              {isZh ? '未找到相关申请' : 'No Application Found'}
            </p>
            <p className="text-xs text-amber-600/70 mt-0.5">
              {isZh ? '请确认邮箱地址，或前往左侧提交合作申请' : 'Please verify your email, or submit an application on the left'}
            </p>
          </div>
        </div>
      )}

      {queried && record && (
        <div className="space-y-5">
          {/* Org info */}
          <div className="flex items-center gap-3 p-3.5 rounded-xl bg-[#0A2540]/4 border border-[#0A2540]/8">
            <div className="w-9 h-9 rounded-lg bg-[#0A2540] flex items-center justify-center shrink-0">
              <span className="text-white text-xs font-bold">
                {record.orgName.slice(0, 2).toUpperCase()}
              </span>
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-[#0A2540] truncate">{record.orgName}</p>
              <p className="text-xs text-[#0A2540]/45">
                {isZh ? '提交于' : 'Submitted'} {new Date(record.submittedAt).toLocaleDateString(isZh ? 'zh-CN' : 'en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
              </p>
            </div>
            <div className="ml-auto shrink-0">
              <span className="px-2 py-1 rounded-full bg-green-50 text-green-600 text-[10px] font-semibold border border-green-200/60 whitespace-nowrap">
                {isZh ? '进行中' : 'In Progress'}
              </span>
            </div>
          </div>

          {/* Progress stages */}
          <div className="space-y-3">
            <p className="text-xs font-semibold text-[#0A2540]/50 uppercase tracking-widest">
              {isZh ? '审核进度' : 'Review Progress'}
            </p>
            {TRACK_STAGES.map((s, idx) => {
              const isCompleted = idx < stage;
              const isCurrent = idx === stage;
              const isPending = idx > stage;
              return (
                <div key={s.labelEn} className={`flex items-start gap-3 p-3.5 rounded-xl transition-all ${isCurrent ? 'bg-[#0A2540]/6 border border-[#0A2540]/12' : 'bg-transparent'}`}>
                  {/* Icon circle */}
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 mt-0.5 transition-all ${isCompleted ? 'bg-green-500' : isCurrent ? 'bg-[#0A2540]' : 'bg-[#0A2540]/10'}`}>
                    <div className="w-4 h-4 flex items-center justify-center">
                      {isCompleted ? (
                        <i className="ri-check-line text-white text-xs" />
                      ) : (
                        <i className={`${s.icon} text-xs ${isCurrent ? 'text-white' : 'text-[#0A2540]/40'}`} />
                      )}
                    </div>
                  </div>
                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-sm font-semibold ${isPending ? 'text-[#0A2540]/30' : 'text-[#0A2540]'}`}>
                        {isZh ? s.labelZh : s.labelEn}
                      </span>
                      {isCurrent && (
                        <span className="px-1.5 py-0.5 rounded-full bg-[#0A2540] text-white text-[9px] font-bold whitespace-nowrap">
                          {isZh ? '当前阶段' : 'CURRENT'}
                        </span>
                      )}
                      {isCompleted && (
                        <span className="text-[10px] text-green-500 font-medium whitespace-nowrap">
                          {isZh ? '✓ 已完成' : '✓ Done'}
                        </span>
                      )}
                    </div>
                    <p className={`text-xs mt-0.5 ${isPending ? 'text-[#0A2540]/25' : 'text-[#0A2540]/50'}`}>
                      {isZh ? s.descZh : s.descEn}
                    </p>
                  </div>
                  {/* Connector line */}
                </div>
              );
            })}
          </div>

          {/* Tip */}
          <div className="flex items-start gap-2 p-3 rounded-lg bg-[#2C7DA0]/6">
            <div className="w-4 h-4 flex items-center justify-center shrink-0 mt-0.5">
              <i className="ri-time-line text-[#2C7DA0] text-sm" />
            </div>
            <p className="text-xs text-[#0A2540]/60 leading-relaxed">
              {isZh
                ? '通常审核周期为 5-10 个工作日，结果将以邮件形式发送至您的申请邮箱，请留意查收。'
                : 'The review typically takes 5-10 business days. Results will be sent to your application email — please check your inbox.'}
            </p>
          </div>
        </div>
      )}

      {/* No submission yet - general hint */}
      {!queried && (
        <div className="text-center py-6">
          <div className="w-16 h-16 rounded-2xl bg-[#0A2540]/4 flex items-center justify-center mx-auto mb-3">
            <div className="w-8 h-8 flex items-center justify-center">
              <i className="ri-mail-send-line text-[#0A2540]/30 text-2xl" />
            </div>
          </div>
          <p className="text-xs text-[#0A2540]/35">
            {isZh ? '已提交合作申请？输入邮箱即可查看当前审核进度' : 'Already applied? Enter your email to check review status'}
          </p>
        </div>
      )}
    </div>
  );
}

export default function PartnershipApplyForm() {
  const { t, i18n } = useTranslation();
  const isZh = i18n.language === 'zh';
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [orgType, setOrgType] = useState('');
  const [domain, setDomain] = useState('');
  const [intention, setIntention] = useState('');
  const [inView, setInView] = useState(false);
  const [activePanel, setActivePanel] = useState<'apply' | 'track'>('apply');
  const [captchaValue, setCaptchaValue] = useState('');
  const [captchaValid, setCaptchaValid] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);
  const submittedEmailRef = useRef('');
  const submittedOrgRef = useRef('');

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const orgTypeOptions = [
    { value: 'university', key: 'partnersPage.apply.form.orgType.university' },
    { value: 'research', key: 'partnersPage.apply.form.orgType.research' },
    { value: 'enterprise', key: 'partnersPage.apply.form.orgType.enterprise' },
    { value: 'nonprofit', key: 'partnersPage.apply.form.orgType.nonprofit' },
    { value: 'government', key: 'partnersPage.apply.form.orgType.government' },
  ];

  const domainOptions = [
    { value: 'ai', key: 'partnersPage.apply.form.domain.ai' },
    { value: 'quantum', key: 'partnersPage.apply.form.domain.quantum' },
    { value: 'biotech', key: 'partnersPage.apply.form.domain.biotech' },
    { value: 'climate', key: 'partnersPage.apply.form.domain.climate' },
    { value: 'energy', key: 'partnersPage.apply.form.domain.energy' },
    { value: 'space', key: 'partnersPage.apply.form.domain.space' },
    { value: 'other', key: 'partnersPage.apply.form.domain.other' },
  ];

  const perks = [
    { icon: 'ri-global-line', titleKey: 'partnersPage.apply.perks.p1.title', descKey: 'partnersPage.apply.perks.p1.desc' },
    { icon: 'ri-calendar-event-line', titleKey: 'partnersPage.apply.perks.p2.title', descKey: 'partnersPage.apply.perks.p2.desc' },
    { icon: 'ri-microscope-line', titleKey: 'partnersPage.apply.perks.p3.title', descKey: 'partnersPage.apply.perks.p3.desc' },
    { icon: 'ri-megaphone-line', titleKey: 'partnersPage.apply.perks.p4.title', descKey: 'partnersPage.apply.perks.p4.desc' },
  ];

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (loading || submitted) return;
    if (!captchaValid) {
      alert(isZh ? '请完成验证码验证' : 'Please complete the CAPTCHA verification');
      return;
    }
    const form = e.currentTarget;
    const formData = new FormData(form);
    
    const data = {
      org_name: formData.get('orgName') as string || '',
      org_type: formData.get('orgType') as string || '',
      contact_name: formData.get('contactName') as string || '',
      contact_title: formData.get('contactTitle') as string || '',
      email: formData.get('email') as string || '',
      phone: formData.get('phone') as string || '',
      country: formData.get('country') as string || '',
      domain: formData.get('domain') as string || '',
      intention: formData.get('intention') as string || '',
    };
    
    // Validate required fields
    if (!data.org_name || !data.org_type || !data.contact_name || !data.email || !data.country || !data.domain || !data.intention) {
      alert(isZh ? '请填写所有必填字段' : 'Please fill in all required fields');
      return;
    }
    
    submittedEmailRef.current = data.email;
    submittedOrgRef.current = data.org_name;
    setLoading(true);
    try {
      const response = await fetch('/api/public/applications', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || '提交失败');
      }
      
      if (data.email) saveSubmission(data.email, data.org_name);
      setSubmitted(true);
    } catch (err) {
      console.error('提交申请失败:', err);
      alert(isZh ? '提交失败，请稍后重试' : 'Submission failed, please try again later');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section ref={sectionRef} className="py-20 bg-white" id="apply">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        {/* Header */}
        <div className={`text-center mb-14 transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#0A2540]/8 text-[#0A2540] text-xs font-semibold mb-4 tracking-widest uppercase border border-[#0A2540]/10">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-links-line" />
            </div>
            {t('partnersPage.apply.badge')}
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-[#0A2540] mb-3">
            {t('partnersPage.apply.title')}
          </h2>
          <p className="text-[#0A2540]/50 text-base max-w-lg mx-auto">
            {t('partnersPage.apply.subtitle')}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 items-start">
          {/* Left: Perks */}
          <div className={`lg:col-span-2 space-y-5 transition-all duration-700 delay-150 ${inView ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}>
            <h3 className="text-lg font-bold text-[#0A2540] mb-6">
              {t('partnersPage.apply.perks.title')}
            </h3>
            {perks.map((perk, idx) => (
              <div
                key={perk.titleKey}
                className={`flex items-start gap-4 transition-all duration-500 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
                style={{ transitionDelay: inView ? `${200 + idx * 80}ms` : '0ms' }}
              >
                <div className="w-10 h-10 rounded-xl bg-[#0A2540]/6 flex items-center justify-center shrink-0">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className={`${perk.icon} text-[#0A2540] text-lg`} />
                  </div>
                </div>
                <div>
                  <div className="font-semibold text-[#0A2540] text-sm mb-0.5">
                    {t(perk.titleKey)}
                  </div>
                  <div className="text-[#0A2540]/55 text-sm leading-relaxed">
                    {t(perk.descKey)}
                  </div>
                </div>
              </div>
            ))}

            {/* Decorative stats */}
            <div className={`mt-8 grid grid-cols-2 gap-4 transition-all duration-700 delay-500 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
              {[
                { num: '30+', label: '覆盖国家' },
                { num: '150+', label: '合作机构' },
                { num: '5000+', label: '参与专家' },
                { num: '50+', label: '年度活动' },
              ].map((s) => (
                <div key={s.label} className="bg-[#F7F9FC] rounded-xl p-4 text-center">
                  <div className="text-2xl font-bold text-[#0A2540]">{s.num}</div>
                  <div className="text-xs text-[#0A2540]/50 mt-0.5">{s.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Right: Panel with tab switcher */}
          <div className={`lg:col-span-3 transition-all duration-700 delay-300 ${inView ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}>
            {/* Tab switcher */}
            <div className="flex items-center gap-1 bg-[#F0F2F5] p-1 rounded-xl mb-5">
              <button
                onClick={() => setActivePanel('apply')}
                className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${activePanel === 'apply' ? 'bg-white text-[#0A2540]' : 'text-[#0A2540]/50 hover:text-[#0A2540]'}`}
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-send-plane-line" />
                </div>
                {isZh ? '提交申请' : 'Apply Now'}
              </button>
              <button
                onClick={() => setActivePanel('track')}
                className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${activePanel === 'track' ? 'bg-white text-[#0A2540]' : 'text-[#0A2540]/50 hover:text-[#0A2540]'}`}
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-map-pin-time-line" />
                </div>
                {isZh ? '进度追踪' : 'Track Progress'}
              </button>
            </div>

            <div className="bg-[#F7F9FC] rounded-2xl p-7 md:p-9">
              {activePanel === 'track' ? (
                <ApplicationTracker isZh={isZh} />
              ) : submitted ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <div className="w-16 h-16 rounded-full bg-green-50 flex items-center justify-center mb-5">
                    <div className="w-8 h-8 flex items-center justify-center">
                      <i className="ri-checkbox-circle-line text-3xl text-green-500" />
                    </div>
                  </div>
                  <h3 className="text-xl font-bold text-[#0A2540] mb-2">
                    {t('partnersPage.apply.form.success')}
                  </h3>
                  <p className="text-[#0A2540]/50 text-sm max-w-xs mb-2">
                    {isZh ? '审核结果将通过邮件发送，预计 5-10 个工作日。' : 'Review results will be sent via email within 5-10 business days.'}
                  </p>
                  <button
                    onClick={() => { setActivePanel('track'); }}
                    className="mt-4 px-5 py-2.5 rounded-lg bg-[#0A2540] text-white text-sm font-medium hover:bg-[#0A2540]/85 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2"
                  >
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-map-pin-time-line" />
                    </div>
                    {isZh ? '查看申请进度' : 'Track My Application'}
                  </button>
                  <button
                    onClick={() => setSubmitted(false)}
                    className="mt-3 px-5 py-2 rounded-lg border border-[#0A2540]/20 text-[#0A2540] text-sm hover:bg-[#0A2540]/5 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    {t('common.close')}
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">
                  {/* Org Name + Type */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-[#0A2540]/70 mb-1.5">
                        {t('partnersPage.apply.form.orgName')}
                      </label>
                      <input
                        name="orgName"
                        required
                        type="text"
                        placeholder={t('partnersPage.apply.form.ph.orgName')}
                        className="w-full px-3 py-2.5 rounded-lg border border-[#0A2540]/15 bg-white text-sm text-[#0A2540] placeholder-[#0A2540]/30 focus:outline-none focus:ring-2 focus:ring-[#0A2540]/20 transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-[#0A2540]/70 mb-1.5">
                        {t('partnersPage.apply.form.orgType')}
                      </label>
                      <select
                        name="orgType"
                        required
                        value={orgType}
                        onChange={(e) => setOrgType(e.target.value)}
                        className="w-full px-3 py-2.5 rounded-lg border border-[#0A2540]/15 bg-white text-sm text-[#0A2540] focus:outline-none focus:ring-2 focus:ring-[#0A2540]/20 transition-all cursor-pointer appearance-none"
                      >
                        <option value="">— {t('partnersPage.apply.form.orgType')} —</option>
                        {orgTypeOptions.map((o) => (
                          <option key={o.value} value={o.value}>{t(o.key)}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Contact Name + Title */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-[#0A2540]/70 mb-1.5">
                        {t('partnersPage.apply.form.contactName')}
                      </label>
                      <input
                        name="contactName"
                        required
                        type="text"
                        placeholder={t('partnersPage.apply.form.ph.contactName')}
                        className="w-full px-3 py-2.5 rounded-lg border border-[#0A2540]/15 bg-white text-sm text-[#0A2540] placeholder-[#0A2540]/30 focus:outline-none focus:ring-2 focus:ring-[#0A2540]/20 transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-[#0A2540]/70 mb-1.5">
                        {t('partnersPage.apply.form.contactTitle')}
                      </label>
                      <input
                        name="contactTitle"
                        type="text"
                        placeholder={t('partnersPage.apply.form.ph.contactTitle')}
                        className="w-full px-3 py-2.5 rounded-lg border border-[#0A2540]/15 bg-white text-sm text-[#0A2540] placeholder-[#0A2540]/30 focus:outline-none focus:ring-2 focus:ring-[#0A2540]/20 transition-all"
                      />
                    </div>
                  </div>

                  {/* Email + Phone */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-[#0A2540]/70 mb-1.5">
                        {t('partnersPage.apply.form.email')}
                      </label>
                      <input
                        name="email"
                        required
                        type="email"
                        placeholder={t('partnersPage.apply.form.ph.email')}
                        className="w-full px-3 py-2.5 rounded-lg border border-[#0A2540]/15 bg-white text-sm text-[#0A2540] placeholder-[#0A2540]/30 focus:outline-none focus:ring-2 focus:ring-[#0A2540]/20 transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-[#0A2540]/70 mb-1.5">
                        {t('partnersPage.apply.form.phone')}
                      </label>
                      <input
                        name="phone"
                        type="tel"
                        placeholder={t('partnersPage.apply.form.ph.phone')}
                        className="w-full px-3 py-2.5 rounded-lg border border-[#0A2540]/15 bg-white text-sm text-[#0A2540] placeholder-[#0A2540]/30 focus:outline-none focus:ring-2 focus:ring-[#0A2540]/20 transition-all"
                      />
                    </div>
                  </div>

                  {/* Country + Domain */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-[#0A2540]/70 mb-1.5">
                        {t('partnersPage.apply.form.country')}
                      </label>
                      <input
                        name="country"
                        required
                        type="text"
                        placeholder={t('partnersPage.apply.form.ph.country')}
                        className="w-full px-3 py-2.5 rounded-lg border border-[#0A2540]/15 bg-white text-sm text-[#0A2540] placeholder-[#0A2540]/30 focus:outline-none focus:ring-2 focus:ring-[#0A2540]/20 transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-[#0A2540]/70 mb-1.5">
                        {t('partnersPage.apply.form.domain')}
                      </label>
                      <select
                        name="domain"
                        required
                        value={domain}
                        onChange={(e) => setDomain(e.target.value)}
                        className="w-full px-3 py-2.5 rounded-lg border border-[#0A2540]/15 bg-white text-sm text-[#0A2540] focus:outline-none focus:ring-2 focus:ring-[#0A2540]/20 transition-all cursor-pointer appearance-none"
                      >
                        <option value="">— {t('partnersPage.apply.form.domain')} —</option>
                        {domainOptions.map((o) => (
                          <option key={o.value} value={o.value}>{t(o.key)}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Intention */}
                  <div>
                    <label className="block text-xs font-semibold text-[#0A2540]/70 mb-1.5">
                      {t('partnersPage.apply.form.intention')}
                    </label>
                    <textarea
                      name="intention"
                      required
                      rows={4}
                      maxLength={500}
                      value={intention}
                      onChange={(e) => setIntention(e.target.value)}
                      placeholder={t('partnersPage.apply.form.ph.intention')}
                      className="w-full px-3 py-2.5 rounded-lg border border-[#0A2540]/15 bg-white text-sm text-[#0A2540] placeholder-[#0A2540]/30 focus:outline-none focus:ring-2 focus:ring-[#0A2540]/20 transition-all resize-none"
                    />
                    <div className="text-right text-xs text-[#0A2540]/30 mt-1">{intention.length}/500</div>
                  </div>

                  {/* CAPTCHA */}
                  <div>
                    <label className="block text-xs font-semibold text-[#0A2540]/70 mb-1.5">
                      {isZh ? '验证码' : 'CAPTCHA'} <span className="text-red-500">*</span>
                    </label>
                    <CaptchaInput
                      value={captchaValue}
                      onChange={setCaptchaValue}
                      onValidate={setCaptchaValid}
                      isZh={isZh}
                    />
                  </div>

                  {/* Submit */}
                  <button
                    type="submit"
                    disabled={loading || !captchaValid}
                    className="w-full py-3 rounded-xl bg-[#0A2540] hover:bg-[#0A2540]/85 text-white font-semibold text-sm transition-all duration-200 cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {loading ? (
                      <span className="flex items-center justify-center gap-2">
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-loader-4-line animate-spin" />
                        </div>
                        {t('common.loading')}
                      </span>
                    ) : (
                      <span className="flex items-center justify-center gap-2">
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-send-plane-line" />
                        </div>
                        {t('partnersPage.apply.form.submit')}
                      </span>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
